import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AprosButton from 'components/aprosButton/index';
import Proctitle from 'components/proctitle/index';
import Linked from 'components/linked/index';
import dayjs from 'dayjs';
import { sapRegister } from 'utils/sapSdkUtil';
import { useDispatch, useSelector } from 'react-redux';
import { accountActions } from 'slices/accountSlice';
import Icons from 'images/icons';
import Path from 'constants/routePath';
import { useDeviceSize } from 'hooks/useDeviceSize';
import { verifyLogin } from 'utils/verifyLoginUtil';
import { getOneTimeToken } from 'utils/axiosUtil';
import Title from '../../constants/title';
import { Helmet } from 'react-helmet';

const GuestReservation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isPc } = useDeviceSize();
  const { isMobila } = useSelector(state => state.mobila);
  const token = useSelector(state => state.account.token);
  const maintenanceInfo = useSelector(state => state.maintenance.maintenanceInfo);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // SAP認証でログインチェック
    const checkLogin = async () => {
      const result = await verifyLogin(dispatch, null);
      if (result) {
        // ログイン済みの場合はTOPへリダイレクト
        window.location.href = `${process.env.REACT_APP_BASE_URL}${Path.TOP}?p10=021&p11=101`;
      } else {
        // 未ログインの場合はローディングを終了
        setIsLoading(false);
      }
    };
    checkLogin();
  }, [dispatch, navigate]);

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center backdrop-blur-[3px]">
        <Icons.Image
          src={Icons.loading}
          className="h-[60px] w-[60px] sm:h-[80px] sm:w-[80px] lg:h-[100px] lg:w-[100px]"
        />
      </div>
    );
  }

  // ゲスト予約へ進むボタン押下時の処理
  const handleGuestReserveClick = async () => {
    // トークンがすでに存在する場合は何もしない
    if (!token) {
      // トークンがない場合は新たに取得
      const oneTimeToken = await getOneTimeToken();
      dispatch(accountActions.updateToken({ token: oneTimeToken }));
    }
    navigate(Path.GUEST_SHOP_AND_DATE_SELECT);
  };

  return (
    <div className="mt-4 lg:px-20">
      <Helmet>
          <title>{Title.GUEST_RESERVATION}</title>
      </Helmet>
      {/* メンテナンスエリア */}
      {maintenanceInfo?.display && (
        <div className="flex flex-col justify-between gap-y-1.5 border border-solid border-line bg-white px-2 py-3 sm:gap-y-4 sm:px-6 sm:py-4 lg:mt-3 lg:gap-y-6">
          <p className="text-sm font-medium text-navy">
            平素はオートバックスの各種サービスをご愛顧頂き、誠にありがとうございます。
            <br />
            下記日程にてサイトのメンテナンスを行います。
            <br />
            皆様には、ご不便をお掛けいたしますが、時間を改めて、アクセス頂きます様、宜しくお願い申し上げます。
          </p>
          <p className="whitespace-pre-wrap text-[14px] font-bold text-navy sm:text-[18px] lg:text-[20px]">
            メンテナンス予定日時{isPc ? '：' : '\n'}
            {dayjs(maintenanceInfo?.maintenanceStartDate).format('M月D日 H:mm')}
            ～{dayjs(maintenanceInfo?.maintenanceEndDate).format('M月D日 H:mm')}{' '}
            予定
          </p>
        </div>
      )}
      <div className="mt-6 sm:hidden">
        <Proctitle
          useH1={true}
          text="車買取査定をWEBで予約"
          className={{ textClass: 'whitespace-nowrap' }}
        />
      </div>
      <div className="mt-6 hidden text-center sm:block">
        <h1 className="text-lg whitespace-nowrap font-bold">
          車買取査定をWEBで予約
        </h1>
      </div>
      <div className="mt-6 sm:justify-center sm:text-center">
        <p className="text-sm">
          オートバックス店舗での査定・買取の予約を行います。
        </p>
      </div>
      <div className="relative mt-6 flex justify-center border border-line bg-white rounded max-w-[640px] w-full mx-auto">
        <div className="flex flex-col items-center">
          <p className="mt-6 mb-4 font-bold sm:text-[24px]">オートバックス会員の方</p>
          <AprosButton
            text="ログインして予約へ進む"
            comClassName="main"
            onClick={() => {
              window.location.href = `${process.env.REACT_APP_BASE_URL}${Path.TOP}?p10=021&p11=101`;
            }}
            className="mb-6 w-[260px]"
          />
        </div>
      </div>
      <div className="relative mt-6 flex justify-center border border-line bg-white rounded max-w-[640px] w-full mx-auto">
        <div className="flex flex-col items-center p-6">
          <p className="mb-4 font-bold sm:text-[24px]">オートバックス会員以外の方</p>
          <p className="mb-4 text-[14px]">登録不要で予約できます</p>
          <AprosButton
            text="ゲスト予約へ進む"
            comClassName="sub"
            onClick={handleGuestReserveClick}
            className="w-[260px] hover:text-navy"
          />
          <div className="my-4 flex w-full items-center">
            <div className="flex-grow border-t border-line w-46 sm:w-32"></div>
            <span className="mx-2">または</span>
            <div className="flex-grow border-t border-line w-46 sm:w-32"></div>
          </div>
          <Linked
            text="新規会員登録"
            onClick={() => sapRegister(window.location.href, isMobila)}
            isLine
            linkIcon="R"
            className="font-bold"
          />
        </div>
      </div>
      <div className="mt-10 flex justify-center">
        <Linked
          text="出張査定についてはこちら"
          onClick={() =>
            window.open(
              'https://www.autobacs.com/static_html/cars/kaitori/visit_assessment.html'
            )
          }
          isLine
          linkIcon="R"
          className="font-bold"
        />
      </div>
      <div className="mt-6 mb-12 flex justify-center sm:mb-24">
        <Linked
          text="オートバックスカーズの車買取について"
          onClick={() =>
            window.open(
              'https://www.autobacs.com/static_html/cars/kaitori/top.html'
            )
          }
          isLine
          linkIcon="R"
          className="font-bold"
        />
      </div>
    </div>
  );
};

export default GuestReservation;
